import { useMemo, useState } from "react";
import type { IFormation } from "@/types/formation";

// ----------------------------------------------------------------------
// Shared catalog filtering — single source of truth for the filter state,
// options and client-side filtering applied on top of the formations API.
// Used by both the main catalog (Formations/FormationCatalog) and the
// entrepreneurship catalog (Entrepreneurship/Catalog).
// ----------------------------------------------------------------------

export type CatalogMode = "Présentiel" | "Distanciel" | "Mixte";
/** Sales format — matched against `IFormation.formats` (see FormationResource):
 *  INTER = individual pricing, INTRA = group pricing. */
export type CatalogFormatMode = "INTER" | "INTRA";
export type DurationOption = "less-1d" | "1d" | "2d" | "3d" | "more-3d" | "custom";

export const CATALOG_MODES: CatalogMode[] = ["Présentiel", "Distanciel", "Mixte"];

export const CATALOG_FORMAT_MODES: CatalogFormatMode[] = ["INTER", "INTRA"];

export const FINANCING_OPTIONS: string[] = ["Opco", "Autres"];

export interface DurationOptionDef {
  value: DurationOption;
  label: string;
  /** Half-open `[min, max)` bucket in days. Absent on "custom", which filters on session dates instead. */
  days?: [number, number];
}

export const DURATION_OPTIONS: DurationOptionDef[] = [
  { value: "less-1d", label: "Moins de 1 jour", days: [0, 1] },
  { value: "1d", label: "1 jour", days: [1, 2] },
  { value: "2d", label: "2 jours", days: [2, 3] },
  { value: "3d", label: "3 jours", days: [3, 4] },
  { value: "more-3d", label: "Plus de 3 jours", days: [4, Infinity] },
  { value: "custom", label: "Dans cette période :" },
];

export const ALL_LOCATIONS: string[] = [
  "Paris",
  "Lyon",
  "Marseille",
  "Bordeaux",
  "Toulouse",
  "Nantes",
  "Lille",
  "Strasbourg",
  "Nice",
  "À distance",
];

export const PRICE_MIN = 96;
export const PRICE_MAX = 3600;
export const DEFAULT_PRICE_RANGE: [number, number] = [PRICE_MIN, PRICE_MAX];

/** Extracts the numeric value from a formatted price string, e.g. "540.00€ HT" → 540. */
const parsePrice = (str: string): number => Number(str.replace(/[^0-9.]/g, "") || 0);

/** A training day, used to convert `duration_hours` when a center left `duration_days` empty. */
const HOURS_PER_DAY = 7;

/** Formation length in days, whichever of the two duration fields is filled in. */
const durationInDays = (f: IFormation): number =>
  f.duration_days ?? f.duration_hours / HOURS_PER_DAY;

/** Open-ended bounds so a half-filled period still filters. ISO `YYYY-MM-DD` compares lexicographically. */
const DATE_MIN = "0000-01-01";
const DATE_MAX = "9999-12-31";

export interface CatalogFilterInit {
  query?: string;
  ville?: string;
  mode?: CatalogMode | null;
}

/**
 * Owns every catalog filter (search, mode, financing, price, duration,
 * location, sort…) and applies them client-side to the formations returned
 * by the API — category scoping is already handled server-side.
 */
export function useCatalogFilters(formations: IFormation[], init?: CatalogFilterInit) {
  const [query, setQuery] = useState(init?.query ?? "");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [selectedMode, setSelectedMode] = useState<CatalogMode | null>(init?.mode ?? null);
  const [selectedFormatMode, setSelectedFormatMode] = useState<CatalogFormatMode | null>(null);
  const [selectedFinancing, setSelectedFinancing] = useState<string[]>([]);
  const [selectedVille, setSelectedVille] = useState<string>(init?.ville ?? "all");
  const [selectedDuration, setSelectedDuration] = useState<DurationOption | null>(null);
  const [durationDateFrom, setDurationDateFrom] = useState("");
  const [durationDateTo, setDurationDateTo] = useState("");
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>(DEFAULT_PRICE_RANGE);

  // Collapsible sections of the filter sidebar
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isFinanceOpen, setIsFinanceOpen] = useState(true);
  const [isDurationOpen, setIsDurationOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);

  // Mobile drawers
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const visibleCards = useMemo<IFormation[]>(() => {
    let base = [...formations];
    const q = query.trim().toLowerCase();

    if (q) {
      base = base.filter((f) =>
        [f.title, f.description, f.category, f.location].some((v) =>
          v?.toLowerCase().includes(q),
        ),
      );
    }

    if (selectedMode === "Présentiel") {
      base = base.filter((f) => f.modality?.includes("presentiel"));
    } else if (selectedMode === "Distanciel") {
      base = base.filter(
        (f) => f.modality?.includes("distanciel") || f.modality?.includes("elearning"),
      );
    }

    if (selectedFormatMode) {
      base = base.filter((f) => f.formats?.includes(selectedFormatMode));
    }

    if (selectedFinancing.length > 0) {
      base = base.filter((f) =>
        selectedFinancing.some((label) =>
          f.financing?.toLowerCase().includes(label.toLowerCase()),
        ),
      );
    }

    if (selectedVille !== "all") {
      base = base.filter((f) => f.location === selectedVille);
    }

    if (selectedLocations.length > 0) {
      base = base.filter((f) => selectedLocations.includes(f.location));
    }

    if (selectedDuration === "custom" && (durationDateFrom || durationDateTo)) {
      // Keep formations running at least one session overlapping the chosen period.
      const from = durationDateFrom || DATE_MIN;
      const to = durationDateTo || DATE_MAX;
      base = base.filter((f) =>
        (f.session_dates ?? []).some((s) => s.start <= to && s.end >= from),
      );
    } else if (selectedDuration) {
      // "custom" has no `days` range — an empty period simply filters nothing.
      const days = DURATION_OPTIONS.find((o) => o.value === selectedDuration)?.days;
      if (days) {
        base = base.filter((f) => {
          const d = durationInDays(f);
          return d >= days[0] && d < days[1];
        });
      }
    }

    const [minP, maxP] = priceRange;
    base = base.filter((f) => {
      const p = parsePrice(f.price);
      // p === 0 means "Sur devis" — always kept visible
      return p === 0 || (p >= minP && p <= maxP);
    });

    if (sortOrder) {
      base = [...base].sort((a, b) => {
        const pa = parsePrice(a.price);
        const pb = parsePrice(b.price);
        return sortOrder === "asc" ? pa - pb : pb - pa;
      });
    }

    return base;
  }, [
    formations,
    query,
    selectedMode,
    selectedFormatMode,
    selectedFinancing,
    selectedVille,
    selectedLocations,
    selectedDuration,
    durationDateFrom,
    durationDateTo,
    priceRange,
    sortOrder,
  ]);

  const handleResetFilters = () => {
    setSelectedMode(null);
    setSelectedFormatMode(null);
    setSelectedFinancing([]);
    setSelectedDuration(null);
    setDurationDateFrom("");
    setDurationDateTo("");
    setSelectedLocations([]);
    setPriceRange(DEFAULT_PRICE_RANGE);
  };

  const handleToggleFinancing = (label: string) =>
    setSelectedFinancing((prev) =>
      prev.includes(label) ? prev.filter((f) => f !== label) : [...prev, label],
    );

  const handleRemoveFinancing = (label: string) =>
    setSelectedFinancing((prev) => prev.filter((f) => f !== label));

  const handleToggleLocation = (loc: string) =>
    setSelectedLocations((prev) =>
      prev.includes(loc) ? prev.filter((l) => l !== loc) : [...prev, loc],
    );

  const handleClearPrice = () => setPriceRange(DEFAULT_PRICE_RANGE);

  const handleClearDuration = () => {
    setSelectedDuration(null);
    setDurationDateFrom("");
    setDurationDateTo("");
  };

  return {
    visibleCards,

    query,
    setQuery,
    sortOrder,
    setSortOrder,
    selectedMode,
    setSelectedMode,
    selectedFormatMode,
    setSelectedFormatMode,
    selectedFinancing,
    selectedVille,
    setSelectedVille,
    selectedDuration,
    setSelectedDuration,
    durationDateFrom,
    setDurationDateFrom,
    durationDateTo,
    setDurationDateTo,
    selectedLocations,
    priceRange,
    setPriceRange,

    isPriceOpen,
    setIsPriceOpen,
    isFinanceOpen,
    setIsFinanceOpen,
    isDurationOpen,
    setIsDurationOpen,
    isLocationOpen,
    setIsLocationOpen,

    mobileFilterOpen,
    setMobileFilterOpen,
    mobileSearchOpen,
    setMobileSearchOpen,

    handleResetFilters,
    handleToggleFinancing,
    handleRemoveFinancing,
    handleToggleLocation,
    handleClearPrice,
    handleClearDuration,
  };
}

export type CatalogFiltersController = ReturnType<typeof useCatalogFilters>;
